import { useEffect, useState, useRef } from 'react'
import GoogleMapReact from 'google-map-react'
import { toast } from 'react-toastify'
import api from '../../../services/api'
import Vector from '../../../assets/Vector.svg'
import {
  Image,
  OverlayTrigger,
  Tooltip,
  Modal,
  Button,
  Spinner,
  Badge,
  Card,
} from 'react-bootstrap'
import styles from './styles.module.css'
import { errorApi } from '../../../utils/errorApi'
import { DateTime } from 'luxon'
import RequestModal from '../../RequestModal'
import { useNavigate } from 'react-router-dom'
import useSupercluster from 'use-supercluster'
import { Stack } from 'phosphor-react'
import { filterDeliveryRequestsByInternalTransfers } from '../../../utils/internalTransfers'

const rolesMap = {
  manager: { api: 'delivery-manager', web: 'gestor-entregas' },
  attendant: { api: 'attendant', web: 'atendente' },
}

const statusInfo = {
  on_route: {
    color: '#053C5E',
    translation: 'Em rota',
  },
  delivered: {
    color: 'green',
    translation: 'Entregue',
  },
  canceled: {
    color: 'red',
    translation: 'Cancelado',
  },
  allocated: {
    color: '#00bfff',
    translation: 'Em carregamento',
  },
}

const Marker = ({ children }) => children

function DeliveryFlowMap({ vehicle, role }) {
  const navigate = useNavigate()

  const [requests, setRequests] = useState([])
  const [bounds, setBounds] = useState(null)
  const [zoom, setZoom] = useState(14)
  const [selectedCluster, setSelectedCluster] = useState([])
  const [showClusterModal, setShowClusterModal] = useState(false)
  const [showVehicleModal, setShowVehicleModal] = useState(false)
  const [initialFocusPosition, setInitialFocusPosition] = useState({
    lat: 0,
    lng: 0,
  })
  const [selectedRequest, setSelectedRequest] = useState()

  const [isLoading, setIsLoading] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)

  const cardContainerRef = useRef(null)
  const mapRef = useRef()

  useEffect(() => {
    async function loadData() {
      if (!vehicle.id) return

      try {
        setIsLoading(true)
        let deliveryData

        const { data } = await api.get(
          `/${rolesMap[role].api}/deliveries?requestsData=true&vehicleId=${vehicle.id}&creationOrder=desc&limit=5`,
        )
        if (data && data.length) {
          deliveryData =
            data.find(
              (delivery) => delivery.status !== 'in_loading' && delivery.requests?.length > 0,
            ) ?? data.find((delivery) => delivery.requests?.length > 0)
        }

        const requestsData = deliveryData
          ? filterDeliveryRequestsByInternalTransfers(deliveryData.requests)
          : []
        setRequests(requestsData)
      } catch (err) {
        toast.error(
          errorApi(err, 'Falha ao buscar os dados das entregas! Por favor, tente novamente.'),
        )
      }
      setIsLoading(false)
    }
    loadData()
  }, [role, vehicle.id])

  //calcula posicao inicial de foco do mapa
  useEffect(() => {
    // 1º prioridade: posicao atual do veículo
    if (vehicle && vehicle.lat_current && vehicle.lng_current) {
      if (
        initialFocusPosition.lat !== vehicle.lat_current ||
        initialFocusPosition.lng !== vehicle.lng_current
      ) {
        setInitialFocusPosition({
          lat: vehicle.lat_current,
          lng: vehicle.lng_current,
        })
      }
      return
    }

    // 2º prioridade: posicao da primeira requisicao de entrega em rota
    const firstOnRouteRequest = requests.find((r) => r.status === 'on_route')
    if (firstOnRouteRequest && firstOnRouteRequest.lat && firstOnRouteRequest.lng) {
      setInitialFocusPosition({
        lat: firstOnRouteRequest.lat,
        lng: firstOnRouteRequest.lng,
      })
      return
    }

    // 3ª prioridade: posicao atual do dispositivo que está acessando o Kora
    if ('geolocation' in navigator) {
      //possui permissao pra coletar posicao do user?
      navigator.geolocation.getCurrentPosition(function (position) {
        setInitialFocusPosition({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        })
      })
      return
    }

    toast.warning(
      'Falha ao definir a posição inicial no mapa. É necessária a permissão para a coleta da sua posição atual.',
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle, requests])

  // Re-centraliza o mapa quando a posição do veículo muda
  useEffect(() => {
    if (mapRef.current && vehicle.lat_current && vehicle.lng_current) {
      mapRef.current.panTo({
        lat: vehicle.lat_current,
        lng: vehicle.lng_current,
      })
    }
  }, [vehicle.lat_current, vehicle.lng_current])

  const checkScroll = () => {
    const container = cardContainerRef.current

    if (!container) return

    setIsOverflowing(container.scrollWidth > container.clientWidth)
  }

  useEffect(() => {
    checkScroll()
  }, [showClusterModal])

  const getFormattedRequestDestination = (r) => {
    return `${r.street}, ${r.street_number} - ${r.neighborhood}, ${r.city} - ${r.state}`
  }

  /* Date must be a ISO string. Returns a date distance from now. */
  function getDateDistanceFromNow(date) {
    const dateDistance = DateTime.fromISO(date).toRelative({ locale: 'pt-BR' })
    return dateDistance
  }

  const getRequestsFromCluster = (cluster) => {
    const markers = supercluster.getLeaves(cluster.id, Infinity)

    return requests.filter((request) => markers.some((marker) => marker.id === request.id))
  }

  const handleClusterClick = (cluster) => {
    const newRequests = getRequestsFromCluster(cluster)
    setSelectedCluster(newRequests)
    setShowClusterModal(true)
  }

  const points = requests
    ? requests.map((request) => ({
        type: 'Feature',
        id: request.id,
        properties: {
          cluster: false,
          receiver_name: request.receiver_name,
          status: request.status,
          order: request.order,
          street: request.street,
          street_number: request.street_number,
          neighborhood: request.neighborhood,
          city: request.city,
          state: request.state,
          lat: request.lat,
          lng: request.lng,
          color: statusInfo[request.status].color,
        },
        geometry: {
          type: 'Point',
          coordinates: [request.lng, request.lat],
        },
      }))
    : []

  const { clusters, supercluster } = useSupercluster({
    points,
    bounds,
    zoom,
    options: { radius: 5, maxZoom: 20 },
  })

  if (isLoading) {
    return (
      <div className={styles.loadingSpinner}>
        <Spinner
          animation="border"
          size="md"
          style={{
            color: '#053C5E',
          }}
        />
      </div>
    )
  }

  if (vehicle && initialFocusPosition.lat && initialFocusPosition.lng) {
    return (
      <div className={`${styles.map} px-1 px-md-3`}>
        <GoogleMapReact
          bootstrapURLKeys={{
            key: 'AIzaSyAfqczu7x3_zOGMgaI1Ipxjk4KIsuPtAdU',
          }}
          center={initialFocusPosition}
          zoom={14}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => {
            mapRef.current = map
            map.setOptions({
              zoomControlOptions: {
                position: maps.ControlPosition.TOP_LEFT, // Define a posição como superior esquerda
              },
            })
          }}
          onChange={({ zoom, bounds }) => {
            setZoom(zoom)
            setBounds([bounds.nw.lng, bounds.se.lat, bounds.se.lng, bounds.nw.lat])
          }}
          options={{ fullscreenControl: false }}
        >
          {clusters.map((cluster) => {
            const [longitude, latitude] = cluster.geometry.coordinates
            const {
              cluster: isCluster,
              receiver_name: receiverName,
              status,
              order,
            } = cluster.properties

            if (isCluster) {
              return (
                <Marker key={`cluster-${cluster.id}`} lat={latitude} lng={longitude}>
                  <div className={styles.clusterMarker} onClick={() => handleClusterClick(cluster)}>
                    <Stack size={20} color="black" weight="bold" />
                  </div>
                  <div className={styles.clusterTooltip}>
                    {getRequestsFromCluster(cluster).map((request) => (
                      <OverlayTrigger
                        key={`request_${cluster.id}_overlay_tooltip_${request.id}`}
                        overlay={
                          <Tooltip id={`tooltip-${request.receiver_name}-2`}>
                            Entrega de <strong>{request.receiver_name}</strong>.<br></br>
                            Status:{' '}
                            <Badge
                              bg="unset"
                              color="white"
                              pill
                              className={styles.statusBadge}
                              style={{
                                backgroundColor: statusInfo[request.status].color,
                              }}
                            >
                              {statusInfo[request.status].translation}
                            </Badge>
                            <hr />
                            {getFormattedRequestDestination(request)}
                          </Tooltip>
                        }
                      >
                        <div key={request.id}>
                          <span
                            onClick={() => setSelectedRequest(request)}
                            style={{
                              backgroundColor: statusInfo[request.status].color,
                            }}
                            className={styles.clusterTooltipRequest}
                          >
                            {request.order || '?'}
                          </span>
                        </div>
                      </OverlayTrigger>
                    ))}
                  </div>
                </Marker>
              )
            }

            return (
              <Marker
                key={`request_${cluster.id}_${cluster.order ?? 0}_${latitude}_${longitude}`}
                lat={latitude}
                lng={longitude}
              >
                <OverlayTrigger
                  key={`request_${cluster.id}_overlay`}
                  overlay={
                    <Tooltip id={`tooltip-${receiverName}`}>
                      Entrega de <strong>{receiverName}</strong>.<br></br>
                      Status:{' '}
                      <Badge
                        bg="unset"
                        color="white"
                        pill
                        className={styles.statusBadge}
                        style={{
                          backgroundColor: statusInfo[status].color,
                        }}
                      >
                        {statusInfo[status].translation}
                      </Badge>
                      <hr />
                      {getFormattedRequestDestination(cluster.properties)}
                    </Tooltip>
                  }
                >
                  <div
                    className={styles.requestCircle}
                    style={{
                      backgroundColor: statusInfo[status].color,
                    }}
                    onClick={() => setSelectedRequest(requests.find((r) => r.id === cluster.id))}
                  >
                    <p className={styles.requestOrderP}>{order || '?'}</p>
                  </div>
                </OverlayTrigger>
              </Marker>
            )
          })}
          {vehicle && vehicle.lat_current && vehicle.lng_current && (
            <Marker
              key={`vehicle_${vehicle.id}_${vehicle.lat_current}_${vehicle.lng_current}`}
              lat={vehicle.lat_current}
              lng={vehicle.lng_current}
            >
              <OverlayTrigger
                key={vehicle.board}
                overlay={
                  <Tooltip id={`tooltip-${vehicle.board}`}>
                    Veículo <strong>{vehicle.board}</strong>.
                    <hr />
                    Última atualização {getDateDistanceFromNow(vehicle.updated_at)}
                  </Tooltip>
                }
              >
                <Image
                  style={{ cursor: 'pointer', marginLeft: -30, marginTop: -30 }}
                  width={60}
                  src={Vector}
                  onClick={() => setShowVehicleModal(true)}
                />
              </OverlayTrigger>
            </Marker>
          )}
        </GoogleMapReact>
        <Modal show={showClusterModal} onHide={() => setShowClusterModal(false)} centered size="xl">
          <Modal.Header>
            <Modal.Title style={{ fontSize: 16, margin: '0 auto' }}>
              Detalhamento de entregas
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div
              ref={cardContainerRef}
              style={{ justifyContent: isOverflowing ? 'flex-start' : 'center' }}
              className={styles.clusterRequestsContainer}
            >
              {selectedCluster.length > 0 &&
                selectedCluster.map((request, index) => (
                  <Card key={index} className={styles.clusterRequestContainer}>
                    <div className={styles.clusterRequestInfoContainer}>
                      <div
                        className={styles.clusterRequest}
                        style={{ backgroundColor: statusInfo[request.status].color }}
                      >
                        <div>{request.order}</div>
                      </div>
                      <div className={styles.clusterRequestInfo}>
                        <p className={styles.requestInfoText}>
                          <strong>Status:</strong>{' '}
                          <Badge
                            bg="unset"
                            color="white"
                            pill
                            className={styles.statusBadge}
                            style={{
                              backgroundColor: statusInfo[request.status].color,
                            }}
                          >
                            {statusInfo[request.status].translation}
                          </Badge>
                        </p>
                        <p className={styles.requestInfoText}>
                          <strong>Destinatário:</strong> {request.receiver_name}
                        </p>
                        <p className={styles.requestInfoText}>
                          <strong>Endereço:</strong> {getFormattedRequestDestination(request)}
                        </p>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      style={{ fontSize: 14 }}
                      className={`${styles.modalBtn} ${styles.detailsBtn}`}
                      onClick={() => {
                        setShowClusterModal(false)
                        setSelectedRequest(request)
                      }}
                    >
                      Ver detalhes
                    </Button>
                  </Card>
                ))}
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex justify-content-center">
            <Button
              onClick={() => setShowClusterModal(false)}
              className={`${styles.modalBtn} ${styles.closeBtn}`}
            >
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>
        <MapLegend />

        {vehicle && vehicle.updated_at && (
          <Modal
            centered
            size="md"
            show={showVehicleModal}
            onHide={() => setShowVehicleModal(false)}
          >
            <Modal.Header closeButton />
            <Modal.Body style={{ marginLeft: 20, marginRight: 20 }}>
              <p style={{ fontWeight: 'bold', fontSize: 14 }} className="text-center mb-1">
                {`Última atualização: ${getDateDistanceFromNow(vehicle.updated_at)}`}
              </p>
              <div className="text-center mb-3">
                <p>
                  {vehicle.model} {vehicle.color} | Placa {vehicle.board}
                </p>
                <Button
                  className={styles.detailsButton}
                  onClick={() =>
                    navigate(`/${rolesMap[role].web}/veiculos/info/${vehicle.id}/entregas-do-dia`)
                  }
                >
                  Ver todas entregas de hoje do veículo
                </Button>
              </div>
            </Modal.Body>
          </Modal>
        )}
        {selectedRequest && (
          <RequestModal
            request={selectedRequest}
            userRole={role}
            showEditButton={false}
            showRequestModal={selectedRequest}
            setShowRequestModal={() => setSelectedRequest(undefined)}
          ></RequestModal>
        )}
      </div>
    )
  } else return <></>
}

// Componente para as legendas
const MapLegend = () => {
  const statusLegend = [
    ...Object.values(statusInfo).map((key) => key),
    { translation: 'Veículo', icon: Vector }, // Ícone do veículo
    {
      translation: 'Grupo de entregas',
      icon: <Stack size={15} style={{ marginRight: 8 }} color="black" />,
    }, // Ícone do cluster
  ]

  return (
    <div className={styles.mapLegendContainer}>
      <h5 className={styles.legendHeader}>Legenda</h5>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {statusLegend.map(({ translation, color, icon }) => (
          <li key={translation} className={styles.legendItem}>
            {icon ? (
              <>
                {typeof icon === 'object' ? (
                  icon
                ) : (
                  <img src={icon} alt={translation} className={styles.legendIcon} />
                )}
              </>
            ) : (
              <span
                style={{
                  display: 'inline-block',
                  width: '15px',
                  height: '15px',
                  backgroundColor: color,
                  marginRight: '8px',
                  borderRadius: '50%',
                }}
              ></span>
            )}
            <span style={{ fontSize: '14px' }}>{translation}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default DeliveryFlowMap
